// MERGE-shape extractor — split out from extractQueryStructure.dml.ts to keep
// per-file budgets in check. MERGE has the most complex grammar of the four
// DML kinds: target + source FROM-items, ON predicate, then a sequence of
// WHEN [NOT] MATCHED [AND cond] THEN <action> branches.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Query Structure Extractor — DML.

import type { ParseOptions, StatementInfo, IdentifierRef, QualifiedName } from '../../sql-grammar/model';
import { rangeFromOffsets, type LineIndex } from '../../sql-grammar/document/lineIndex';
import { skipContextFor } from '../classify/tokenSkip';
import { foldIdentifier, readIdentifier, readQualifiedName } from '../scope/identifiers';
import type {
  AliasBinding,
  FromItem,
  MergeBranch,
  MergeBranchAction,
  UpdateAssignment,
} from '../types';

import { findTopLevelCommas, scanTopLevelKeywords, skipParen, trimRange, type TopKeyword } from './scanner';
import { extractSelectStructure, type SelectExtractCtx } from './extractQueryStructure.select';
import { parseBooleanFragments } from './extractQueryStructure.predicates';
import { parseAssignmentsRange, readDmlTarget, type MutableQueryStructure } from './extractQueryStructure.dml';

// eslint-disable-next-line complexity, max-lines-per-function
export function extractMergeStructure(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): MutableQueryStructure {
  const { source, options } = ctx;
  const kws = scanTopLevelKeywords(source, start, end, options);
  const mergeIdx = kws.findIndex((k) => k.upper === 'MERGE');
  if (mergeIdx < 0) return {};
  const intoIdx = kws.findIndex((k, i) => i > mergeIdx && k.upper === 'INTO');
  if (intoIdx < 0) return {};
  const target = readDmlTarget(ctx, kws[intoIdx].end, end);
  if (!target) return {};

  const usingIdx = kws.findIndex((k, i) => i > intoIdx && k.upper === 'USING');
  if (usingIdx < 0) return { mergeTarget: target.item } as MutableQueryStructure;
  const onIdx = kws.findIndex((k, i) => i > usingIdx && k.upper === 'ON');
  const usingEnd = onIdx >= 0 ? kws[onIdx].start : end;
  const source_ = readMergeSource(ctx, kws[usingIdx].end, usingEnd);

  const out: MutableQueryStructure = { mergeTarget: target.item };
  if (source_) out.mergeSource = source_;

  // Locate WHEN keywords as branch boundaries.
  const whenIndices: number[] = [];
  for (let k = 0; k < kws.length; k++) if (kws[k].upper === 'WHEN') whenIndices.push(k);
  const firstWhenStart = whenIndices.length > 0 ? kws[whenIndices[0]].start : end;

  if (onIdx >= 0) {
    const onEnd = firstWhenStart;
    const fragments = parseBooleanFragments(ctx, kws[onIdx].end, onEnd);
    if (fragments) out.mergeOn = fragments;
  }

  // Split into branches; for each WHEN, range = [whenStart, nextWhenStart).
  const matched: MergeBranch[] = [];
  const notMatched: MergeBranch[] = [];
  for (let i = 0; i < whenIndices.length; i++) {
    const whenKw = kws[whenIndices[i]];
    const next = i + 1 < whenIndices.length ? kws[whenIndices[i + 1]].start : end;
    const branch = parseMergeBranch(ctx, whenKw.end, next);
    if (!branch) continue;
    if (branch.notMatched) notMatched.push(branch.branch);
    else matched.push(branch.branch);
  }
  if (matched.length > 0) out.mergeWhenMatched = matched;
  if (notMatched.length > 0) out.mergeWhenNotMatched = notMatched;
  return out;
}

interface BranchRead {
  readonly branch: MergeBranch;
  readonly notMatched: boolean;
}

// Parse one branch starting AFTER `WHEN`: optionally `NOT`, then `MATCHED`,
// then optional `AND <cond>`, then `THEN <action>`.
// eslint-disable-next-line complexity, max-lines-per-function
function parseMergeBranch(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): BranchRead | null {
  const { source, options, lineIdx } = ctx;
  let i = start;
  while (i < end && /\s/.test(source[i])) i++;
  let notMatched = false;
  let id = readIdentifier(source, i, end, options);
  if (id && id.value.toUpperCase() === 'NOT') {
    notMatched = true;
    i = id.end;
    while (i < end && /\s/.test(source[i])) i++;
    id = readIdentifier(source, i, end, options);
  }
  if (!id || id.value.toUpperCase() !== 'MATCHED') return null;
  i = id.end;

  // Optional `AND <cond> ` until THEN.
  const branchKws = scanTopLevelKeywords(source, i, end, options);
  const thenKw = branchKws.find((k) => k.upper === 'THEN');
  if (!thenKw) return null;
  let condition: string | undefined;
  const andKw = branchKws.find((k) => k.upper === 'AND');
  if (andKw && andKw.start < thenKw.start) {
    const condText = source.slice(andKw.end, thenKw.start).trim();
    if (condText) condition = condText;
  }

  // Action body is between THEN and end-of-branch.
  const actionStart = thenKw.end;
  const action = parseMergeAction(ctx, actionStart, end);
  if (!action) return null;

  const branch: MergeBranch = {
    range: rangeFromOffsets(lineIdx, start, end),
    action: action.action,
    ...(condition !== undefined ? { condition } : {}),
    ...(action.updateAssignments ? { updateAssignments: action.updateAssignments } : {}),
    ...(action.insertColumns ? { insertColumns: action.insertColumns } : {}),
    ...(action.insertValues ? { insertValues: action.insertValues } : {}),
  };
  return { branch, notMatched };
}

interface ActionRead {
  readonly action: MergeBranchAction;
  readonly updateAssignments?: readonly UpdateAssignment[];
  readonly insertColumns?: readonly IdentifierRef[];
  readonly insertValues?: readonly string[];
}

// eslint-disable-next-line complexity
function parseMergeAction(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): ActionRead | null {
  const { source, options, lineIdx } = ctx;
  let i = start;
  while (i < end && /\s/.test(source[i])) i++;
  const verb = readIdentifier(source, i, end, options);
  if (!verb) return null;
  const upper = verb.value.toUpperCase();

  if (upper === 'DELETE') return { action: 'delete' };
  if (upper === 'DO') {
    let p = verb.end;
    while (p < end && /\s/.test(source[p])) p++;
    const next = readIdentifier(source, p, end, options);
    if (next && next.value.toUpperCase() === 'NOTHING') return { action: 'do-nothing' };
    return null;
  }
  if (upper === 'UPDATE') {
    // UPDATE SET col=val, ...
    let p = verb.end;
    while (p < end && /\s/.test(source[p])) p++;
    const setIdent = readIdentifier(source, p, end, options);
    if (!setIdent || setIdent.value.toUpperCase() !== 'SET') return { action: 'update' };
    const assigns = parseAssignmentsRange(source, options, lineIdx, setIdent.end, end);
    return { action: 'update', ...(assigns.length ? { updateAssignments: assigns } : {}) };
  }
  if (upper === 'INSERT') {
    // INSERT [(cols)] VALUES (val, ...)
    let p = verb.end;
    while (p < end && /\s/.test(source[p])) p++;
    let insertColumns: readonly IdentifierRef[] | undefined;
    if (source[p] === '(') {
      const close = skipParen(source, p, end, skipContextFor(options));
      const cols = readColumns(source, options, lineIdx, p + 1, close > 0 ? close - 1 : end);
      if (cols.length > 0) insertColumns = cols;
      p = close;
    }
    while (p < end && /\s/.test(source[p])) p++;
    const valuesIdent = readIdentifier(source, p, end, options);
    let insertValues: readonly string[] | undefined;
    if (valuesIdent && valuesIdent.value.toUpperCase() === 'VALUES') {
      let q = valuesIdent.end;
      while (q < end && /\s/.test(source[q])) q++;
      if (source[q] === '(') {
        const close = skipParen(source, q, end, skipContextFor(options));
        const innerStart = q + 1;
        const innerEnd = close > 0 ? close - 1 : end;
        const commas = findTopLevelCommas(source, innerStart, innerEnd, options);
        const cells: string[] = [];
        let s = innerStart;
        for (const c of commas) { cells.push(source.slice(s, c).trim()); s = c + 1; }
        cells.push(source.slice(s, innerEnd).trim());
        insertValues = cells;
      }
    }
    return {
      action: 'insert',
      ...(insertColumns ? { insertColumns } : {}),
      ...(insertValues ? { insertValues } : {}),
    };
  }
  return null;
}

function readColumns(
  source: string,
  options: ParseOptions,
  lineIdx: LineIndex,
  start: number,
  end: number,
): readonly IdentifierRef[] {
  const commas = findTopLevelCommas(source, start, end, options);
  const out: IdentifierRef[] = [];
  let s = start;
  const push = (a: number, b: number): void => {
    const t = trimRange(source, a, b, options);
    if (t.startOffset >= t.endOffset) return;
    const ident = readIdentifier(source, t.startOffset, t.endOffset, options);
    if (!ident) return;
    const qualified: QualifiedName = {
      name: ident.value,
      raw: source.slice(t.startOffset, t.endOffset),
      quoted: ident.quoted,
      foldedName: foldIdentifier(ident.value, ident.quoted, options),
    };
    out.push({
      qualified,
      range: rangeFromOffsets(lineIdx, t.startOffset, t.endOffset),
      kind: 'column',
    });
  };
  for (const c of commas) { push(s, c); s = c + 1; }
  push(s, end);
  return out;
}

// MERGE source can be a relation or a derived `(SELECT …) src`.
// eslint-disable-next-line complexity
function readMergeSource(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): FromItem | null {
  const { source, options, lineIdx, buffer } = ctx;
  let i = start;
  while (i < end && /\s/.test(source[i])) i++;
  if (i >= end) return null;

  if (source[i] === '(') {
    const close = skipParen(source, i, end, skipContextFor(options));
    const innerStart = i + 1;
    const innerEnd = close > 0 ? close - 1 : end;
    const subStmt: StatementInfo = {
      id: `merge-source-${innerStart}-${innerEnd}`,
      range: rangeFromOffsets(lineIdx, innerStart, innerEnd),
      kind: 'select', risk: 'read', readsFrom: [], writesTo: [], schemaEffects: [],
    };
    const sub = extractSelectStructure(buffer, subStmt);
    const p = close;
    const alias = readAlias(ctx, p, end);
    return {
      range: rangeFromOffsets(lineIdx, i, alias?.endOffset ?? p),
      ...(alias ? { alias: alias.binding } : {}),
      target: { kind: 'derived-table', subquery: sub },
    };
  }

  const qn = readQualifiedName(source, i, end, options);
  if (!qn) return null;
  const p = qn.range.endOffset;
  const alias = readAlias(ctx, p, end);
  return {
    range: rangeFromOffsets(lineIdx, i, alias?.endOffset ?? p),
    ...(alias ? { alias: alias.binding } : {}),
    target: {
      kind: 'relation',
      qualified: qn.qualified,
      nameRange: rangeFromOffsets(lineIdx, qn.terminalRange.startOffset, qn.terminalRange.endOffset),
      partCount: qn.partCount,
    },
  };
}

interface AliasRead {
  readonly binding: AliasBinding;
  readonly endOffset: number;
}

function readAlias(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): AliasRead | null {
  const { source, options, lineIdx } = ctx;
  let i = start;
  while (i < end && /\s/.test(source[i])) i++;
  let aliasIdent = readIdentifier(source, i, end, options);
  if (aliasIdent && !aliasIdent.quoted && aliasIdent.value.toUpperCase() === 'AS') {
    let r = aliasIdent.end;
    while (r < end && /\s/.test(source[r])) r++;
    aliasIdent = readIdentifier(source, r, end, options);
  }
  if (!aliasIdent) return null;
  if (!aliasIdent.quoted) {
    const u = aliasIdent.value.toUpperCase();
    if (u === 'ON' || u === 'USING' || u === 'WHEN') return null;
  }
  const binding: AliasBinding = {
    alias: aliasIdent.value,
    aliasFolded: foldIdentifier(aliasIdent.value, aliasIdent.quoted, options),
    target: { name: aliasIdent.value, raw: aliasIdent.raw, quoted: false, foldedName: foldIdentifier(aliasIdent.value, aliasIdent.quoted, options) },
    source: 'from',
    range: rangeFromOffsets(lineIdx, aliasIdent.start, aliasIdent.end),
  };
  return { binding, endOffset: aliasIdent.end };
}

// Re-export needed helpers from scanner so consumers don't need a second import.
export { findTopLevelCommas, scanTopLevelKeywords, type TopKeyword };
